import html2canvas from "html2canvas";
import { getChartImageDataUrl } from "./chartImage";

const getBoxTitle = (el) => el.closest(".box")?.querySelector("h2, h3")?.textContent?.trim();

const getPrecedingSectionTitle = (parentEl) => {
  const prev = parentEl?.previousElementSibling;
  if (!prev) return null;
  if (prev.tagName === "H2" || prev.tagName === "H3") return prev.textContent?.trim();
  return prev.querySelector?.("h2, h3")?.textContent?.trim() || null;
};

// pdfmake ตัดบรรทัดโดยอิงช่องว่างเป็นหลัก (ไม่รู้จักขอบเขตคำภาษาไทยที่เขียนติดกัน) ข้อความยาว ๆ ที่ไม่มี
// เว้นวรรคเลยจึงล้นกรอบได้ แก้โดยตัดข้อความเป็น "กลุ่มตัวอักษร" (พยัญชนะฐาน + สระ/วรรณยุกต์ที่ประกอบกัน)
// แล้วส่งเป็น array ของ {text: cluster} ให้ pdfmake - ตัดบรรทัดระหว่างแต่ละ object ได้เองโดยไม่ต้องมี

const toThaiClusters = (text) => {
  const clusters = [];
  for (const ch of text) {
    if (/\p{Mn}/u.test(ch) && clusters.length) {
      clusters[clusters.length - 1] += ch;
    } else {
      clusters.push(ch);
    }
  }
  return clusters;
};

const softWrapThai = (text) => toThaiClusters(text).map((cluster) => ({ text: cluster }));


let colorCanvasCtx;
const colorToHex = (color) => {
  if (!color) return null;
  if (/^#[0-9a-f]{6}$/i.test(color)) return color;
  if (!colorCanvasCtx) colorCanvasCtx = document.createElement("canvas").getContext("2d");
  colorCanvasCtx.fillStyle = "#000000";
  colorCanvasCtx.fillStyle = color;
  const normalized = colorCanvasCtx.fillStyle;
  if (/^#[0-9a-f]{6}$/i.test(normalized)) return normalized;
  const parts = normalized.match(/[\d.]+/g);
  if (!parts || parts.length < 3) return null;
  return `#${parts.slice(0, 3).map((n) => Math.round(Number(n)).toString(16).padStart(2, "0")).join("")}`;
};

// StatCard
const STAT_GROUP_IMAGE_SCALE = 2; // ความคมชัด 2 เท่า กัน blur ตอนขยายรูปใน PDF
const STAT_GROUP_IMAGE_WIDTH_LANDSCAPE = 500; // ใกล้เต็มความกว้างเนื้อหาของหน้า A4 (~523pt) เผื่อขอบไว้เล็กน้อย
const STAT_GROUP_IMAGE_WIDTH_PORTRAIT = STAT_GROUP_IMAGE_WIDTH_LANDSCAPE / 2;
const STAT_GROUP_IMAGE_MAX_HEIGHT = 350;

const buildStatGroupContent = async (parentEl, title) => {
  const canvas = await html2canvas(parentEl, {
    backgroundColor: "#ffffff",
    scale: STAT_GROUP_IMAGE_SCALE,
  });
  const isLandscape = canvas.width >= canvas.height;
  const width = isLandscape ? STAT_GROUP_IMAGE_WIDTH_LANDSCAPE : STAT_GROUP_IMAGE_WIDTH_PORTRAIT;

  const content = [];
  if (title) content.push({ text: title, style: "chartTitle" });
  content.push({ image: canvas.toDataURL("image/png"), fit: [width, STAT_GROUP_IMAGE_MAX_HEIGHT], margin: [0, 0, 0, 16] });
  return content;
};

// List (เช่น "ปัญหาเด่น")

const buildListContent = (el) => {
  const content = [];
  const title = el.querySelector("h2, h3")?.textContent?.trim();
  if (title) content.push({ text: title, style: "chartTitle" });

  const isHighlightList = title === "ปัญหาเด่น";
  let itemIndex = 0;
  el.querySelectorAll("li").forEach((li) => {
    const lines = Array.from(li.querySelectorAll("p"))
      .map((p) => p.textContent?.trim())
      .filter(Boolean);
    if (!lines.length) return;

    const isFirstHighlightItem = isHighlightList && itemIndex === 0;
    content.push({
      text: [
        { text: `${lines[0]}\n`, bold: true, fontSize: 11, color: isFirstHighlightItem ? "#DC2626" : undefined },
        { text: lines.slice(1).join(" "), fontSize: 9, color: "#4B5563" },
      ],
      margin: [0, 0, 0, 6],
    });
    itemIndex++;
  });
  return content;
};


// Chart

const getChartLegendItems = (canvasEl) => {
  const legendUl = canvasEl.parentElement?.nextElementSibling?.querySelector("ul");
  if (!legendUl) return [];
  return Array.from(legendUl.querySelectorAll("li")).map((li) => {
    const spans = li.querySelectorAll("span");
    return {
      color: colorToHex(spans[0]?.style?.backgroundColor) || "#9CA3AF",
      label: spans[1]?.textContent?.trim() || "",
      percent: spans[2]?.textContent?.trim() || "",
    };
  });
};

const LEGEND_ITEMS_PER_ROW = 2;
const LEGEND_SWATCH_SIZE = 8;


const buildLegendItem = (item) => ({
  columns: [
    {
      width: LEGEND_SWATCH_SIZE,
      canvas: [{ type: "rect", x: 0, y: 2, w: LEGEND_SWATCH_SIZE, h: LEGEND_SWATCH_SIZE, color: item.color }],
    },
    {
      width: "*",
      text: toThaiClusters(item.percent ? `${item.label} ${item.percent}` : item.label).map((cluster) => ({
        text: cluster,
        fontSize: 9,
        color: "#4B5563",
      })),
    },
  ],
  columnGap: 4,
});

const buildLegendContent = (items) => {
  if (!items.length) return null;
  const rows = [];
  for (let i = 0; i < items.length; i += LEGEND_ITEMS_PER_ROW) {
    rows.push(items.slice(i, i + LEGEND_ITEMS_PER_ROW));
  }
  return {
    stack: rows.map((row) => ({
      columns: row.map(buildLegendItem),
      columnGap: 16,
      margin: [0, 2, 0, 2],
    })),
    margin: [0, 4, 0, 10],
  };
};

const CHART_WIDTH_SOLO = 480;
const CHART_WIDTH_PAIRED_RATIO = 0.78;
const CHART_MAX_HEIGHT = 450;
const CHART_WITH_LEGEND_WIDTH = 320;

const CHART_MAX_HEIGHT_OVERRIDES = {
  "สรุปสภาพปัญหาแยกรายมิติ": 300,
};
const buildChartContent = async (el, { paired = false } = {}) => {
  const dataUrl = await getChartImageDataUrl(el);
  if (!dataUrl) return null;
  const content = [];
  const title = getBoxTitle(el);
  if (title) content.push({ text: title, style: "chartTitle" });

  const legendItems = el.tagName === "CANVAS" ? getChartLegendItems(el) : [];
  const baseWidth = legendItems.length ? CHART_WITH_LEGEND_WIDTH : CHART_WIDTH_SOLO;
  const imageWidth = paired ? Math.round(baseWidth * CHART_WIDTH_PAIRED_RATIO) : baseWidth;
  const maxHeight = CHART_MAX_HEIGHT_OVERRIDES[title] || CHART_MAX_HEIGHT;
  content.push({
    image: dataUrl,
    fit: [imageWidth, maxHeight],
    alignment: legendItems.length || paired ? "center" : undefined,
    margin: [0, 0, 0, legendItems.length ? 4 : 16],
  });

  const legendContent = buildLegendContent(legendItems);
  if (legendContent) content.push(legendContent);

  return content;
};

// ProgressList

const PROGRESS_BAR_WIDTH = 460;

const buildProgressRow = (li) => {
  const row = li.children[0]; // div แถวบน: label + ข้อความเปอร์เซ็นต์
  const spans = row ? row.querySelectorAll("span") : [];
  const label = spans[0]?.textContent?.trim() || "";
  const rightText = spans[1]?.textContent?.trim().replace(/\s+/g, " ") || "";

  const track = li.children[1]; // div แถบพื้นหลังสีเทา
  const fillEl = track?.children?.[0]; // div แถบสีที่ fill ตามเปอร์เซ็นต์
  const percent = Math.max(0, Math.min(100, parseFloat(fillEl?.style?.width) || 0));
  const color = colorToHex(fillEl?.style?.backgroundColor) || "#1C4E80";

  return {
    stack: [
      {
        columns: [
          { text: softWrapThai(label), fontSize: 9, width: "*" },
          { text: rightText, fontSize: 8, color: "#6B7280", width: "auto" },
        ],
        columnGap: 6,
      },
      {
        canvas: [
          { type: "rect", x: 0, y: 2, w: PROGRESS_BAR_WIDTH, h: 6, color: "#E7E7E7" },
          { type: "rect", x: 0, y: 2, w: (PROGRESS_BAR_WIDTH * percent) / 100, h: 6, color },
        ],
        margin: [0, 2, 0, 10],
      },
    ],
  };
};

const buildProgressListContent = (el) => {
  const content = [];
  const title = getBoxTitle(el);
  if (title) content.push({ text: title, style: "chartTitle" });
  el.querySelectorAll("li").forEach((li) => content.push(buildProgressRow(li)));
  return content;
};

// จัดกลุ่มเนื้อหาเป็นหน้ากระดาษ แล้วแปลงเป็น content เดียวสำหรับ pdfmake

const PROGRESS_LISTS_PER_PAGE = 2;
const CHARTS_PER_PAGE = 2;

const buildPageGroups = async (rootEl) => {
  const els = rootEl.querySelectorAll(
    "[data-chart-export], [data-progress-export], [data-pdf-stat], [data-pdf-list]"
  );

  const groups = [];
  let pending = [];
  let statGroupParent = null;
  let progressBuffer = [];
  let chartBuffer = [];

  let firstStatGroupSeen = false;
  let firstStatGroupStash = null;

  const flushFirstStatGroupStash = () => {
    if (firstStatGroupStash) {
      pending.push(...firstStatGroupStash);
      firstStatGroupStash = null;
    }
  };

  const flushStatGroup = async () => {
    if (statGroupParent) {
      const title = getPrecedingSectionTitle(statGroupParent);
      const content = await buildStatGroupContent(statGroupParent, title);
      if (!firstStatGroupSeen) {
        firstStatGroupSeen = true;
        firstStatGroupStash = content;
      } else {
        pending.push(...content);
      }
    }
    statGroupParent = null;
  };

  const flushProgressBuffer = () => {
    if (progressBuffer.length) {
      groups.push([...pending, ...progressBuffer.flat()]);
      pending = [];
      progressBuffer = [];
    }
  };

  const flushChartBuffer = () => {
    if (chartBuffer.length) {
      groups.push(chartBuffer.flat());
      chartBuffer = [];
    }
  };

  for (const el of els) {
    if (el.hasAttribute("data-pdf-stat")) {
      flushProgressBuffer();
      flushChartBuffer();
      if (statGroupParent && el.parentElement !== statGroupParent) await flushStatGroup();
      statGroupParent = el.parentElement;
      continue;
    }

    await flushStatGroup();

    if (el.hasAttribute("data-pdf-list")) {
      flushProgressBuffer();
      flushChartBuffer();
      const listContent = buildListContent(el);
      if (firstStatGroupStash) {
        pending.push({
          columns: [
            { width: "*", stack: firstStatGroupStash },
            { width: "*", stack: listContent },
          ],
          columnGap: 16,
        });
        firstStatGroupStash = null;
      } else {
        pending.push(...listContent);
      }
      continue;
    }

    if (el.hasAttribute("data-progress-export")) {
      flushFirstStatGroupStash();
      flushChartBuffer();
      progressBuffer.push(buildProgressListContent(el));
      if (progressBuffer.length >= PROGRESS_LISTS_PER_PAGE) flushProgressBuffer();
      continue;
    }

    // data-chart-export
    flushFirstStatGroupStash();
    flushProgressBuffer();
    const hasStatOrListPending = pending.length > 0;
    const chartContent = await buildChartContent(el, { paired: !hasStatOrListPending });
    if (!chartContent) continue;

    if (hasStatOrListPending) {
      flushChartBuffer();
      groups.push([...pending, ...chartContent]);
      pending = [];
    } else {
      chartBuffer.push(chartContent);
      if (chartBuffer.length >= CHARTS_PER_PAGE) flushChartBuffer();
    }
  }

  await flushStatGroup();
  flushFirstStatGroupStash();
  flushProgressBuffer();
  flushChartBuffer();
  if (pending.length) groups.push(pending);

  return groups;
};


const flattenPageGroups = (groups) => {
  const content = [];
  groups.forEach((group, groupIndex) => {
    group.forEach((block, blockIndex) => {
      const isFirstBlockOfLaterGroup = groupIndex > 0 && blockIndex === 0;
      content.push(isFirstBlockOfLaterGroup ? { ...block, pageBreak: "before" } : block);
    });
  });
  return content;
};

export const buildDashboardPdfContent = async (rootEl) => flattenPageGroups(await buildPageGroups(rootEl));
